/**
 * Workspace API-key scope catalog.
 *
 * The scope list is generated (./generated/api-scopes.ts, from the route surface the API
 * enforces), so it cannot fall behind. expandApiKeyScopes on the API still accepts
 * preset:read|write|full; this UI emits concrete scopes so the user can see and tick each verb.
 */
import { API_SCOPES } from './generated/api-scopes.js';

export type ScopeVerb = 'read' | 'write' | 'delete';

export interface ScopeGroup {
  readonly resource: string;
  readonly label: string;
  readonly hint: string;
  readonly verbs: readonly ScopeVerb[];
}

const HINTS: Record<string, string> = {
  connections: 'Infer, approve and manage source and target connections.',
  mappings: 'Field mappings and versions between two connections.',
  pipelines: 'Activate and configure sync pipelines.',
  'pipeline-events': 'Read pipeline activity events.',
  events: 'Realtime SSE stream and stream tickets.',
  notifications: 'In-app notification feed.',
  jobs: 'Read inference, rematch and sync jobs.',
  unified: 'Unified records API.',
  'golden-records': 'Live golden record set.',
  archival: 'Cold / archived records and policy.',
  onboarding: 'Wizard session for connect → map → activate.',
  settings: 'Workspace settings including API keys.',
  secrets: 'Stored credentials. Prefer read-only unless rotating.',
  webhooks: 'Outbound webhook subscriptions.',
};

const LEAD = [
  'connections', 'mappings', 'pipelines', 'pipeline-events', 'events', 'notifications',
  'jobs', 'unified', 'golden-records', 'archival', 'onboarding', 'settings',
];

function humanise(resource: string): string {
  return resource.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function groupFrom(scopes: readonly string[]): ScopeGroup[] {
  const by = new Map<string, Set<ScopeVerb>>();
  for (const s of scopes) {
    const i = s.lastIndexOf(':');
    if (i < 0) continue;
    const resource = s.slice(0, i);
    const verb = s.slice(i + 1) as ScopeVerb;
    if (!by.has(resource)) by.set(resource, new Set());
    if (verb === 'read' || verb === 'write' || verb === 'delete') by.get(resource)!.add(verb);
  }
  const order = [...LEAD, ...[...by.keys()].filter((k) => !LEAD.includes(k)).sort()];
  return order.filter((r) => by.has(r)).map((resource) => {
    const verbs = (['read', 'write', 'delete'] as const).filter((v) => by.get(resource)!.has(v));
    return {
      resource,
      label: humanise(resource),
      hint: HINTS[resource] ?? `Access to ${humanise(resource).toLowerCase()}.`,
      verbs,
    };
  });
}

export { API_SCOPES };

export const SCOPE_GROUPS: readonly ScopeGroup[] = groupFrom(API_SCOPES);

export const PRESETS = [
  { id: 'read', label: 'Read only', hint: 'Every published read scope. Safe default for probing.' },
  { id: 'write', label: 'Read and write', hint: 'Read plus create and update. No delete.' },
  { id: 'full', label: 'Full access', hint: 'Every published scope, including delete. Treat like a root key.' },
] as const;

export function scopesForPreset(kind: 'read' | 'write' | 'full'): string[] {
  const out: string[] = [];
  for (const g of SCOPE_GROUPS) {
    for (const v of g.verbs) {
      if (kind === 'full') out.push(`${g.resource}:${v}`);
      else if (kind === 'write' && v !== 'delete') out.push(`${g.resource}:${v}`);
      else if (kind === 'read' && v === 'read') out.push(`${g.resource}:${v}`);
    }
  }
  return out;
}

export function toggleScope(current: readonly string[], scope: string, on: boolean): string[] {
  const set = new Set(current);
  if (on) set.add(scope);
  else set.delete(scope);
  return [...set].sort();
}

export function setGroupVerb(current: readonly string[], resource: string, verb: ScopeVerb, on: boolean, verbs: readonly ScopeVerb[]): string[] {
  let next = toggleScope(current, `${resource}:${verb}`, on);
  // Write/delete imply read so a key is never mute on that resource.
  if (on && verb !== 'read' && verbs.includes('read')) {
    next = toggleScope(next, `${resource}:read`, true);
  }
  if (!on && verb === 'read') {
    for (const v of verbs) next = toggleScope(next, `${resource}:${v}`, false);
  }
  return next;
}

export function groupSelection(current: readonly string[], resource: string): Record<ScopeVerb, boolean> {
  return {
    read: current.includes(`${resource}:read`),
    write: current.includes(`${resource}:write`),
    delete: current.includes(`${resource}:delete`),
  };
}

export function detectPreset(current: readonly string[]): 'read' | 'write' | 'full' | 'custom' {
  const norm = [...current].sort().join('|');
  for (const kind of ['read', 'write', 'full'] as const) {
    if (scopesForPreset(kind).sort().join('|') === norm) return kind;
  }
  return 'custom';
}
